/**
 * gpu_destroy_shield — destroy GPU buffers only when no command buffer that
 * references them can still reach the queue: full liveness tracking at the
 * WebGPU API boundary.
 *
 * WHY (hyperwarp flood forensics, 2026-08-23): the `__noFree` differential
 * proved the "[Buffer] used in submit while destroyed" flood survives with
 * every app-level deferred free disabled — three r185 ITSELF destroys
 * uniform/attribute buffers (`RenderObject.dispose` inside `RenderObjects.get`
 * during `_renderObjectDirect`). Deferring by submit count (V1) failed: r185
 * defers submits PER RENDER CONTEXT under pipeline-compile pressure, so
 * submit order ≠ encode order. Deferring until every PRE-request command
 * buffer flushed (V2, exact encoder-serial tracking) ALSO failed — the field
 * error then had to come from command buffers encoded AFTER the destroy:
 * a stale cached bind group (post-pass render context) still references the
 * destroyed buffer every frame — the "TSL binds at BUILD time" family. Time
 * can never fix a recurring reference.
 *
 * THE SHIELD (V3, liveness): every `createCommandEncoder` takes a monotonic
 * serial; `createBindGroup` records which buffers a bind group holds;
 * `setBindGroup` / `setVertexBuffer` / `setIndexBuffer` / copies /
 * `writeBuffer` stamp each referenced buffer with the recording encoder's
 * serial. A destroy request holds the buffer until BOTH:
 *   (a) every encoder open or unsubmitted at request time has flushed
 *       (serial watermark), AND
 *   (b) the buffer's last observed use predates the request — a use by a
 *       LATER encoder re-arms the hold (the buffer is still live; destroying
 *       it would flood every subsequent submit).
 * A stale-bound buffer therefore survives until its pass rebuilds, then
 * frees; worst case is briefly-held VRAM + harmless stale data — never a
 * destroyed-in-submit error. Abandoned encoders (exception mid-encode) age
 * out after ABANDON_MS. No unconditional destroy ceiling (the 20 s backstop
 * lesson): WebGPU reclaims undestroyed resources on GC.
 *
 * V4 (bind-group holds): observation-based liveness STILL lost to an
 * INTERMITTENT stale pass (bake-cadence bind group set ~1/s slipped a quiet
 * 250 ms window, then erred on every later use — the field flood exactly).
 * three r185 refcounts its own BindGroup wrappers (`usedTimes`) but nothing
 * refcounts the underlying GPUBuffer across contexts, so `_destroyBindings`
 * can kill a buffer other cached GPUBindGroups still name. The shield
 * rebuilds that refcount at the API boundary: `createBindGroup` counts a
 * hold on every buffer it names; a FinalizationRegistry releases the hold
 * when the bind group is GC'd (unreachable ⇒ unusable ⇒ safe). A held
 * buffer is destroyed only via the IDLE_FALLBACK (no observed use for 5
 * minutes — the no-GC leak guard).
 *
 * V5b: textures ride the SAME machinery. The buffer evidence proved the
 * watermark alone (V2) loses to stale cached bind groups; a bind group can
 * name a destroyed texture VIEW identically (the error's Texture variant).
 * `createView` maps view→texture, bind groups hold the texture through the
 * registry, and render-pass attachments mark their textures used.
 *
 * V6 (2026-08-24 field report — "explored a new system, alt-tabbed away and
 * back, flood"): the idle clocks ran on WALL time, so a hidden tab aged
 * every held buffer past its gate with no frame rendered, and the first
 * frames back drew them destroyed. Now nothing is released while the
 * document is hidden, and on return every hold is re-armed as just-used.
 * And the DIAGNOSTIC REPORT: an `uncapturederror` listener on the device
 * prints a copy-pasteable JSON block ("[GPUShield] destroyed-in-submit
 * report") — shield counters, visibility history, space state, the last
 * released buffers with their destroyer call sites and lifetimes.
 *
 * V7 (2026-08-24 field report — a ground world→world swap): three r185
 * also references buffers through drawIndirect / drawIndexedIndirect
 * (BatchedMesh — the structure pools), dispatchWorkgroupsIndirect,
 * resolveQuerySet (occlusion/timestamp resolve targets) and render bundles
 * (executeBundles) — none of which the V3 draw stamps saw, so an indirect
 * buffer looked "never drawn" and took the fast release path. All are
 * observed now; bundles carry the buffer set their encoder touched.
 *
 * V8 (2026-08-27 field report — space→personal-world→spawn, and a reproducible
 * rapid commons↔personal swap): the destroyer report pinned 21/24 releases on
 * `WebGPUAttributeUtils.destroyAttribute` (three's OWN vertex/index buffers),
 * and a `__leakPlain` differential drove the rapid-swap flood to ZERO — proving
 * the residual is 100% PLAIN (never-bind-grouped) attribute buffers. V7 only
 * STAMPED bundle buffers on execute, so a CACHED bundle (three's RenderBundle
 * path, replayed each frame its render context runs) that sat >PLAIN_IDLE_MS
 * without running — its world culled or mid-swap — let the idle path free a
 * buffer the bundle still named, then the next executeBundles flooded. Fix:
 * bundles now HOLD every buffer they reference for the bundle's LIFETIME via a
 * FinalizationRegistry refcount (the exact V4 bind-group mechanism), so a
 * bundle-reachable attribute buffer is freed only on bundle GC (unreachable ⇒
 * can never execute ⇒ safe) or the 5-min IDLE_FALLBACK. Non-bundle plain buffers
 * keep the fast idle path, so VRAM stays bounded (unlike __leakPlain). A
 * disproven sibling — the `onSubmittedWorkDone` completion fence — was NEUTRAL
 * (the culprits are stale-stamped, idle ~30 s, so no timing gate holds them);
 * missed-stamp reachability, not submit timing, was the defect.
 *
 * V9 (2026-08-27 — the GC-luck correction): V8's bundle-holds measured 0 twice,
 * then the SAME committed code measured 9-23 across later runs — the hold
 * released on FinalizationRegistry timing, i.e. luck. Root model, locked in by
 * the spec: the validation fires at the SUBMIT of a FRESHLY-ENCODED command
 * buffer that binds the destroyed resource — so every flood error means a
 * still-live three cache RE-ENCODED it after our release, and observed revival
 * gaps (~30 s: a world's caches going quiet across a swap, reviving on
 * re-entry) beat EVERY finite idle window. No wait can be right; only
 * reachability can. And the reachability oracle is free: a cache that can
 * re-encode a resource HOLDS a JS reference to its wrapper. So V9 never calls
 * destroy() on anything EVER drawn, bind-grouped, or attached (`everRisky`,
 * stamped at the draw/bind-group/attachment/bundle sites): the shield keeps NO
 * strong ref, and the browser reclaims the VRAM the moment the wrapper is
 * unreachable — which can never race a live reference, by definition. This is
 * the `__leakPlain` diagnostic (which measured ZERO errors) minus its leak:
 * leakPlain pinned entries in `held` forever; V9 drops them. Never-referenced
 * staging/copy scratch keeps the explicit fast-path destroy (copies/writes are
 * re-encoded from live state — the V5 lesson — and are the streaming-pool
 * VRAM that must free promptly). The V8 registries remain as stats plumbing.
 *
 * DIAG: `__shieldOff = true` restores immediate destroys (differential
 * probing — the destroy-stack forensic probe needs ORIGINAL call sites).
 * `__leakPlain = true` never frees plain buffers (isolates the attribute-buffer
 * class). `__shieldStats()` reports counters (`rearmed` = stale-reference holds,
 * `gcReleased` = destroy intents resolved by wrapper-GC).
 */

const MS_MARGIN = 250;        // small wall-clock floor on top of the liveness condition
const ABANDON_MS = 120_000;   // an encoder unfinished this long can never submit

interface Held { obj: object | null; destroy: () => void; watermark: number; atMs: number; site: string; bornMs: number }
interface Released { site: string; kind: string; size: number; bindGrouped: boolean; holds: number; ageMs: number; idleMs: number; heldMs: number; at: number; serial: number }
const RING = 24;

let installed = false;
let nextSerial = 1;
let deferred = 0;
let released = 0;
let rearmed = 0;
let gcReleased = 0;  // V9: destroy intents resolved by wrapper-GC instead of destroy()
let submits = 0;
// serial → creation ms; insertion order is ascending serial, so the first
// live entry is the minimum outstanding serial (Map preserves order).
const unflushed = new Map<number, number>();
let held: Held[] = [];
const releasedRing: Released[] = [];   // the last RING actual destroys (for the report)
let lastSubmitMs = 0;
let lastHiddenAt = 0;                   // when the tab last went hidden
let lastVisibleAt = 0;                  // when it last came back
let reports = 0;
let lastReportMs = 0;
const lastUsed = new WeakMap<object, number>();
const lastUsedMs = new WeakMap<object, number>();
const holdCount = new WeakMap<object, number>();
const everBindGrouped = new WeakSet<object>();
// V9 (2026-08-27): a resource EVER referenced by GPU work that caches can
// replay — a draw bind (vertex/index/indirect/bundle), a bind group, or a
// render-pass attachment — is RISKY: some three cache may re-encode it long
// after any idle window (observed revival gaps ~30 s beat every finite wait,
// and the WebGPU spec fires the validation at the SUBMIT of that fresh
// command buffer). Copies/writeBuffer do NOT mark risky (re-encoded from
// live state each time — the V5 staging lesson).
const everRisky = new WeakSet<object>();
// Only DRAW uses (setVertexBuffer/setIndexBuffer) arm the idle gate: cached
// render lists can replay a stale draw, but copies/writes are re-encoded
// from live state each time — holding copy-marked staging buffers 15 s
// doubled streaming retention in the mappedAtCreation pool for nothing.
const lastDrawMs = new WeakMap<object, number>();

function minUnflushed(): number {
  for (const [serial, atMs] of unflushed) {
    if (performance.now() - atMs > ABANDON_MS) { unflushed.delete(serial); continue; }
    return serial;
  }
  return Infinity;
}

function drain(): void {
  if (held.length === 0) return;
  if ((globalThis as { __holdDisposal?: boolean }).__holdDisposal) return; // world-transition hold: release nothing while submits are paused/resuming
  if (typeof document !== 'undefined' && document.hidden) return; // V6: a hidden tab renders nothing — release nothing
  const now = performance.now();
  const min = minUnflushed();
  const keep: Held[] = [];
  for (const h of held) {
    // V9: only NEVER-risky objects (staging/copy scratch) are queued here; a
    // held object that BECAME risky (drawn/bind-grouped after the destroy
    // request) hands off to wrapper-GC — dropping our strong ref is the
    // release (the browser reclaims VRAM once nothing can bind it).
    if (h.obj && everRisky.has(h.obj)) { gcReleased++; continue; }
    const used = h.obj ? lastUsed.get(h.obj) : undefined;
    if (used !== undefined && used >= h.watermark) {
      // Referenced by an encoder NEWER than the destroy request — still live.
      // Re-arm: wait for the referencing work too.
      h.watermark = nextSerial; h.atMs = now; rearmed++;
      keep.push(h);
      continue;
    }
    if (min < h.watermark || now - h.atMs < MS_MARGIN) { keep.push(h); continue; }
    released++;
    if (h.obj) {
      const o = h.obj as { size?: number; usage?: number; width?: number; height?: number; label?: string };
      const t = lastDrawMs.get(h.obj) ?? lastUsedMs.get(h.obj);
      releasedRing.push({
        site: h.site, kind: typeof o.width === 'number' ? `texture ${o.width}×${o.height ?? 0}${o.label ? ' ' + o.label : ''}` : `buffer usage=${o.usage ?? 0}${o.label ? ' ' + o.label : ''}`,
        size: o.size ?? 0, bindGrouped: everBindGrouped.has(h.obj), holds: holdCount.get(h.obj) ?? 0,
        ageMs: Math.round(now - h.bornMs), idleMs: t === undefined ? -1 : Math.round(now - t), heldMs: Math.round(now - h.atMs), at: Math.round(now), serial: nextSerial,
      });
      if (releasedRing.length > RING) releasedRing.shift();
    }
    try { h.destroy(); } catch { /* device lost / already destroyed */ }
  }
  held = keep;
}

/** V6: the copy-pasteable report printed when the device raises a destroyed-resource error. */
function report(message: string): void {
  const now = performance.now();
  reports++;
  if (reports > 3 && now - lastReportMs < 30_000) return; // the flood repeats per frame — three reports, then one per 30 s
  lastReportMs = now;
  const space = (globalThis as { __space?: { state?: () => Record<string, unknown> } }).__space;
  let spaceState: Record<string, unknown> | null = null;
  try {
    const st = space?.state?.();
    if (st) spaceState = { active: st.active, stage: st.stage, sysJump: st.sysJump, system: (st.system as { id?: string; name?: string } | undefined)?.id ?? st.systemId ?? null, remotes: st.remotes, bolts: st.bolts, wings: st.wings };
  } catch { /* probe surface absent */ }
  const sites = new Map<string, number>();
  for (const r of releasedRing) sites.set(r.site, (sites.get(r.site) ?? 0) + 1);
  const rep = {
    error: message.slice(0, 200),
    shield: { version: 'v9', submits, deferred, released, gcReleased, rearmed, pending: held.length, unflushed: unflushed.size, minSerial: minUnflushed(), nextSerial },
    time: { now: Math.round(now), sinceLastSubmitMs: Math.round(now - lastSubmitMs), sinceLastReleaseMs: releasedRing.length ? Math.round(now - releasedRing[releasedRing.length - 1]!.at) : -1 },
    visibility: { state: typeof document !== 'undefined' ? document.visibilityState : 'n/a', sinceHiddenMs: lastHiddenAt ? Math.round(now - lastHiddenAt) : -1, sinceVisibleMs: lastVisibleAt ? Math.round(now - lastVisibleAt) : -1 },
    space: spaceState,
    recentDestroyers: [...sites.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8).map(([site, count]) => ({ site, count })),
    lastReleased: releasedRing.slice(-12).map((r) => ({ ...r, agoMs: Math.round(now - r.at) })),
    memory: (performance as { memory?: { usedJSHeapSize: number } }).memory ? Math.round((performance as unknown as { memory: { usedJSHeapSize: number } }).memory.usedJSHeapSize / 1048576) + ' MB heap' : 'n/a',
    ua: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 120) : '',
  };
  console.error('[GPUShield] destroyed-in-submit report — copy the block below and paste it in a bug report:\n' + JSON.stringify(rep, null, 1));
}

/** The first non-shield stack frame of a destroy request — names the destroyer. */
function callSite(): string {
  const st = new Error().stack ?? '';
  const lines = st.split('\n').slice(1);
  for (const l of lines) {
    if (l.includes('gpu_destroy_shield')) continue;
    return l.trim().replace(/^at\s+/, '').replace(/\?[tv]=[^:)]+/g, '').slice(0, 140);
  }
  return '?';
}

function wrapDestroy(proto: { destroy?: () => void } | undefined, tracked: boolean): void {
  if (!proto || typeof proto.destroy !== 'function') return;
  const orig = proto.destroy as (this: object) => void;
  const pending = new WeakSet<object>();
  proto.destroy = function (this: object): void {
    if ((globalThis as { __shieldOff?: boolean }).__shieldOff) { orig.call(this); return; }
    if ((globalThis as { __leakAll?: boolean }).__leakAll) return; // DIAG: never destroy anything (decisive bisect)
    if (pending.has(this)) return; // destroy() is idempotent per spec; one hold is enough
    pending.add(this);
    deferred++;
    if (tracked && everRisky.has(this)) {
      // V9: EVER drawn / bind-grouped / attached ⇒ some cache may still encode
      // a reference (revival gaps beat every finite wait — the GC-luck flood).
      // destroy() is what floods; wrapper-GC reclaims the VRAM the moment
      // nothing can bind it, and can NEVER race a live reference. Keep no ref.
      gcReleased++;
      return;
    }
    held.push({ obj: tracked ? this : null, destroy: () => orig.call(this), watermark: nextSerial, atMs: performance.now(), site: callSite(), bornMs: performance.now() });
  };
}

type AnyFn = (this: object, ...args: unknown[]) => unknown;
function wrap(proto: object | undefined, name: string, make: (orig: AnyFn) => AnyFn): void {
  const p = proto as Record<string, AnyFn> | undefined;
  if (!p || typeof p[name] !== 'function') return;
  p[name] = make(p[name]!);
}

/** Install once, before the renderer is created. No-op without WebGPU. */
export function installGpuDestroyShield(): void {
  if (installed) return;
  const g = globalThis as unknown as Record<string, { prototype?: object } | undefined>;
  const dev = g.GPUDevice?.prototype;
  const enc = g.GPUCommandEncoder?.prototype;
  const queue = g.GPUQueue?.prototype;
  if (!dev || !enc || !queue) return; // WebGL fallback — nothing to shield
  installed = true;

  const encoderSerial = new WeakMap<object, number>();
  const cbSerial = new WeakMap<object, number>();
  const passSerial = new WeakMap<object, number>();
  const bgBuffers = new WeakMap<object, object[]>();
  const viewTexture = new WeakMap<object, object>();
  const isBuffer = (x: unknown): x is object =>
    !!x && typeof x === 'object' && x instanceof ((g.GPUBuffer as unknown as new () => object) ?? class {});
  const isView = (x: unknown): x is object =>
    !!x && typeof x === 'object' && x instanceof ((g.GPUTextureView as unknown as new () => object) ?? class {});
  // ONE clock sample per pass / submit. WHY: these wrappers sit inside every
  // setBindGroup / setVertexBuffer / setIndexBuffer / writeBuffer — with
  // r185's per-object UBO that is tens of thousands of calls a frame at the
  // high tiers, each paying performance.now() + WeakMap writes (2026-09-12).
  // The stamps feed age thresholds measured in seconds; pass granularity is exact enough.
  let frameNowMs = performance.now();
  const mark = (buf: unknown, serial: number | undefined): void => {
    if (serial !== undefined && isBuffer(buf)) { lastUsed.set(buf, serial); lastUsedMs.set(buf, frameNowMs); }
  };

  // V6: catch the device to listen for uncaptured errors (three's own
  // onuncapturederror stays — addEventListener rides alongside it).
  wrap(g.GPUAdapter?.prototype, 'requestDevice', (orig) => function (...args) {
    const p = orig.apply(this, args) as Promise<{ addEventListener?: (t: string, f: (e: { error?: { message?: string } }) => void) => void }>;
    void p.then((device) => {
      try {
        device.addEventListener?.('uncapturederror', (e) => {
          const msg = e?.error?.message ?? '';
          if (/destroyed/i.test(msg)) report(msg);
        });
      } catch { /* older device shape */ }
    }).catch(() => { /* the app handles device failure */ });
    return p;
  });
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      const now = performance.now();
      if (document.hidden) { lastHiddenAt = now; return; }
      lastVisibleAt = now;
      // Back from a hidden tab: nothing rendered meanwhile, so every hold is
      // re-armed as just-used — the idle gates restart from here.
      for (const h of held) { h.atMs = now; h.watermark = nextSerial; if (h.obj) { lastDrawMs.set(h.obj, now); lastUsedMs.set(h.obj, now); } }
    });
  }

  wrap(dev, 'createCommandEncoder', (orig) => function (...args) {
    const e = orig.apply(this, args) as object;
    const serial = nextSerial++;
    encoderSerial.set(e, serial);
    unflushed.set(serial, performance.now());
    return e;
  });

  const bgRegistry = new FinalizationRegistry<object[]>((bufs) => {
    for (const b of bufs) holdCount.set(b, Math.max(0, (holdCount.get(b) ?? 0) - 1));
    drain();
  });
  wrap(dev, 'createBindGroup', (orig) => function (...args) {
    const bg = orig.apply(this, args) as object;
    const desc = args[0] as { entries?: Iterable<{ resource?: { buffer?: unknown } }> } | undefined;
    const bufs: object[] = [];
    if (desc?.entries) {
      for (const e of desc.entries) {
        const r = e?.resource as { buffer?: unknown } | undefined;
        if (isBuffer(r?.buffer)) bufs.push(r!.buffer as object);
        else if (isView(r)) { const tex = viewTexture.get(r as object); if (tex) bufs.push(tex); }
      }
    }
    if (bufs.length > 0) {
      bgBuffers.set(bg, bufs);
      for (const b of bufs) { holdCount.set(b, (holdCount.get(b) ?? 0) + 1); everBindGrouped.add(b); everRisky.add(b); }
      bgRegistry.register(bg, bufs);
    }
    return bg;
  });

  const markView = (v: unknown, serial: number | undefined): void => {
    if (serial === undefined || !isView(v)) return;
    const tex = viewTexture.get(v as object);
    if (tex) { lastUsed.set(tex, serial); lastUsedMs.set(tex, performance.now()); everRisky.add(tex); }
  };
  for (const begin of ['beginRenderPass', 'beginComputePass']) {
    wrap(enc, begin, (orig) => function (...args) {
      const pass = orig.apply(this, args) as object;
      frameNowMs = performance.now();
      const serial = encoderSerial.get(this);
      if (serial !== undefined) passSerial.set(pass, serial);
      const desc = args[0] as {
        colorAttachments?: Iterable<{ view?: unknown; resolveTarget?: unknown } | null>;
        depthStencilAttachment?: { view?: unknown };
      } | undefined;
      if (desc?.colorAttachments) {
        for (const c of desc.colorAttachments) { if (c) { markView(c.view, serial); markView(c.resolveTarget, serial); } }
      }
      markView(desc?.depthStencilAttachment?.view, serial);
      return pass;
    });
  }
  for (const copy of ['copyBufferToBuffer', 'copyBufferToTexture', 'copyTextureToBuffer']) {
    wrap(enc, copy, (orig) => function (...args) {
      const serial = encoderSerial.get(this);
      mark(args[0], serial);
      mark((args[0] as { buffer?: unknown })?.buffer, serial);
      mark(args[2], serial);
      mark((args[2] as { buffer?: unknown })?.buffer, serial);
      return orig.apply(this, args);
    });
  }
  wrap(enc, 'finish', (orig) => function (...args) {
    const cb = orig.apply(this, args) as object;
    const serial = encoderSerial.get(this);
    if (serial !== undefined) cbSerial.set(cb, serial);
    return cb;
  });

  for (const passProto of [g.GPURenderPassEncoder?.prototype, g.GPUComputePassEncoder?.prototype]) {
    wrap(passProto, 'setBindGroup', (orig) => function (...args) {
      const serial = passSerial.get(this);
      if (serial !== undefined) {
        const bufs = bgBuffers.get(args[1] as object);
        if (bufs) { for (const b of bufs) { if (lastUsed.get(b) === serial) continue; lastUsed.set(b, serial); lastUsedMs.set(b, frameNowMs); } } // already stamped this pass → skip the writes
      }
      return orig.apply(this, args);
    });
  }
  const markDraw = (buf: unknown, serial: number | undefined): void => {
    if (serial !== undefined && isBuffer(buf)) { lastUsed.set(buf, serial); lastDrawMs.set(buf, frameNowMs); everRisky.add(buf as object); }
  };
  wrap(g.GPURenderPassEncoder?.prototype, 'setVertexBuffer', (orig) => function (...args) {
    markDraw(args[1], passSerial.get(this));
    return orig.apply(this, args);
  });
  wrap(g.GPURenderPassEncoder?.prototype, 'setIndexBuffer', (orig) => function (...args) {
    markDraw(args[0], passSerial.get(this));
    return orig.apply(this, args);
  });

  // V7: indirect draws / dispatches reference their argument buffer like a draw.
  for (const m of ['drawIndirect', 'drawIndexedIndirect']) {
    wrap(g.GPURenderPassEncoder?.prototype, m, (orig) => function (...args) {
      markDraw(args[0], passSerial.get(this));
      return orig.apply(this, args);
    });
  }
  wrap(g.GPUComputePassEncoder?.prototype, 'dispatchWorkgroupsIndirect', (orig) => function (...args) {
    markDraw(args[0], passSerial.get(this));
    return orig.apply(this, args);
  });
  wrap(enc, 'resolveQuerySet', (orig) => function (...args) {
    markDraw(args[3], encoderSerial.get(this)); // the resolve destination buffer
    return orig.apply(this, args);
  });
  // Render bundles: remember every buffer a bundle encoder touched, stamp them
  // whenever the bundle executes (bundles are replayed for many frames).
  const bundleBuffers = new WeakMap<object, Set<object>>();
  const bundleEncBuffers = new WeakMap<object, Set<object>>();
  // V8 (2026-08-27 forensics): a __leakPlain differential proved the residual
  // rapid-swap flood is 100% PLAIN (never-bind-grouped) vertex/index buffers —
  // three r185's own attribute buffers. They fell through because bundles were
  // only STAMPED on execute: a CACHED bundle (three's RenderBundle path replays
  // it every frame its render context runs) that goes >PLAIN_IDLE_MS without
  // running (its world not drawn — culled / mid-swap) let the shield's idle path
  // free a buffer the bundle still names, then the next executeBundles flooded.
  // Fix: HOLD every buffer a bundle references for the bundle's LIFETIME — the
  // exact FinalizationRegistry refcount already trusted for bind groups (V4).
  // A bundle-held buffer is freed only once the bundle is GC'd (unreachable ⇒
  // can never executeBundles ⇒ safe) or the 5-min IDLE_FALLBACK. Non-bundle
  // plain buffers still take the fast idle path, so VRAM stays bounded (unlike
  // __leakPlain, which held everything).
  const bundleRegistry = new FinalizationRegistry<Set<object>>((bufs) => {
    for (const b of bufs) holdCount.set(b, Math.max(0, (holdCount.get(b) ?? 0) - 1));
    drain();
  });
  wrap(dev, 'createRenderBundleEncoder', (orig) => function (...args) {
    const be = orig.apply(this, args) as object;
    bundleEncBuffers.set(be, new Set());
    return be;
  });
  const bproto = g.GPURenderBundleEncoder?.prototype;
  const noteB = (be: object, buf: unknown): void => { if (isBuffer(buf)) { bundleEncBuffers.get(be)?.add(buf as object); everRisky.add(buf as object); } };
  wrap(bproto, 'setVertexBuffer', (orig) => function (...args) { noteB(this, args[1]); return orig.apply(this, args); });
  wrap(bproto, 'setIndexBuffer', (orig) => function (...args) { noteB(this, args[0]); return orig.apply(this, args); });
  for (const m of ['drawIndirect', 'drawIndexedIndirect']) wrap(bproto, m, (orig) => function (...args) { noteB(this, args[0]); return orig.apply(this, args); });
  wrap(bproto, 'setBindGroup', (orig) => function (...args) {
    const bufs = bgBuffers.get(args[1] as object);
    if (bufs) for (const b of bufs) noteB(this, b);
    return orig.apply(this, args);
  });
  wrap(bproto, 'finish', (orig) => function (...args) {
    const bundle = orig.apply(this, args) as object;
    const set = bundleEncBuffers.get(this);
    if (set) {
      bundleBuffers.set(bundle, set);
      if (set.size > 0) {
        for (const b of set) holdCount.set(b, (holdCount.get(b) ?? 0) + 1); // held for the bundle's life
        bundleRegistry.register(bundle, set);
      }
    }
    return bundle;
  });
  wrap(g.GPURenderPassEncoder?.prototype, 'executeBundles', (orig) => function (...args) {
    const serial = passSerial.get(this);
    const list = args[0] as Iterable<object> | undefined;
    if (serial !== undefined && list) {
      const t = performance.now();
      for (const bundle of list) { const set = bundleBuffers.get(bundle); if (set) for (const b of set) { lastUsed.set(b, serial); lastDrawMs.set(b, t); lastUsedMs.set(b, t); } }
    }
    return orig.apply(this, args);
  });

  wrap(queue, 'writeBuffer', (orig) => function (...args) {
    mark(args[0], nextSerial); // conservative: a per-frame-written buffer is live
    return orig.apply(this, args);
  });
  wrap(queue, 'submit', (orig) => function (...args) {
    const r = orig.apply(this, args);
    frameNowMs = performance.now();
    submits++;
    lastSubmitMs = performance.now();
    const cbs = args[0];
    if (Array.isArray(cbs)) {
      for (const cb of cbs) {
        const serial = cbSerial.get(cb as object);
        if (serial !== undefined) unflushed.delete(serial);
      }
    }
    drain(); // post-submit: the flushed CBs validated against still-alive resources
    return r;
  });

  wrap(g.GPUTexture?.prototype, 'createView', (orig) => function (...args) {
    const v = orig.apply(this, args) as object;
    viewTexture.set(v, this);
    return v;
  });
  wrapDestroy(g.GPUBuffer?.prototype as { destroy?: () => void } | undefined, true);
  wrapDestroy(g.GPUTexture?.prototype as { destroy?: () => void } | undefined, true);
  // Sweep entries whose flush condition met while submits went quiet.
  window.setInterval(drain, 1000);
  // Boot breadcrumb: a field console paste must be SELF-ATTRIBUTING — the
  // 2026-08-24 report could not tell whether the erroring session even had
  // the shield. One tagged line settles it forever.
  console.info('[GPUShield] armed v9 — WebGPU destroy shield active (__shieldStats() for counters; a destroyed-in-submit error prints a copyable report)');
  (globalThis as Record<string, unknown>).__shieldStats = () => ({
    version: 'v9',
    submits, deferred, released, gcReleased, rearmed, pending: held.length,
    unflushed: unflushed.size, minSerial: minUnflushed(), nextSerial,
  });
}


/**
 * THE DRAW GUARD (2026-09-01). A single `TypeError: parameter 1 is not of type
 * 'GPUBuffer'` thrown from inside `WebGPUBackend.draw` (three asked the pass
 * encoder to bind a geometry whose GPU data had been deleted — a dispose that
 * landed while the object was still in a render list) unwinds three's render
 * MID-PASS: the command encoder is never finished, and every later frame fails
 * validation on it ("[Texture output] ... same synchronization scope" /
 * "invalid due to a previous error") — one bad draw becomes a dead session.
 * The native call throws BEFORE recording anything, so the encoder is still
 * consistent: skip that one object, name it (copyable, once per geometry) and
 * let the frame finish. Install once the renderer exists.
 */
export function installDrawGuard(renderer: { backend?: unknown } | null | undefined): void {
  const backend = renderer?.backend as ({ draw?: (ro: unknown, info: unknown) => void } & Record<string, unknown>) | undefined;
  if (!backend || typeof backend.draw !== 'function' || backend.__drawGuard) return;
  backend.__drawGuard = true;
  const proto = Object.getPrototypeOf(backend) as { draw: (ro: unknown, info: unknown) => void };
  const orig = proto.draw;
  const seen = new Set<string>();
  let skipped = 0;
  (globalThis as Record<string, unknown>).__drawGuardStats = () => ({ skipped, geometries: [...seen] });
  proto.draw = function (this: unknown, renderObject: unknown, info: unknown) {
    try {
      return orig.call(this, renderObject, info);
    } catch (err) {
      if (!(err instanceof TypeError)) throw err;
      skipped++;
      const ro = renderObject as { object?: Record<string, unknown> & { parent?: Record<string, unknown> | null }; geometry?: Record<string, unknown>; material?: Record<string, unknown> } | undefined;
      const obj = ro?.object;
      const geo = (ro?.geometry ?? obj?.geometry) as Record<string, unknown> | undefined;
      const mat = (ro?.material ?? obj?.material) as Record<string, unknown> | undefined;
      const key = String(geo?.uuid ?? 'unknown');
      if (!seen.has(key) && seen.size < 12) {
        seen.add(key);
        const idx = geo?.index as { array?: { constructor?: { name?: string } }; count?: number } | null | undefined;
        const report = {
          object: (obj?.name as string) || (obj?.type as string), objType: obj?.type,
          parent: (obj?.parent?.name as string) || (obj?.parent?.type as string) || null,
          geometry: (geo?.name as string) || (geo?.type as string), geoUuid: key,
          index: idx ? `${idx.array?.constructor?.name ?? '?'}×${idx.count ?? '?'}` : 'none',
          material: (mat?.name as string) || (mat?.type as string),
          instanced: !!obj?.isInstancedMesh, count: obj?.count ?? null,
          error: String(err).slice(0, 140),
        };
        console.error(`[DrawGuard] skipped a draw whose GPU buffers are gone (the object was disposed while still drawn) — ${JSON.stringify(report)}`, err);
      }
      return; // nothing was recorded for this object: the pass encoder stays valid
    }
  } as typeof proto.draw;
}
